import { CITY } from '@city'
import { useEffect } from 'react'
import type { ReactElement } from 'react'
import { Helmet } from 'react-helmet-async'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '../i18n/locales'
import { localeToOgTag } from './localeToOgTag'

/**
 * SEO head for a localized page.
 *
 * Emits:
 * - `<title>` + `<meta name="description">`
 * - `<link rel="canonical">` pointing at the current locale's URL
 * - `<link rel="alternate" hreflang="<locale>">` for every locale in
 *   {@link PageMetaProps.hreflangLocales} + an `x-default` pointing at
 *   the {@link DEFAULT_LOCALE} URL
 * - Open Graph (`og:type`, `og:locale`, `og:title`, `og:description`, `og:url`)
 * - Twitter Card (`twitter:title`, `twitter:description`)
 *
 * The prerendered HTML already carries the same tags at build time
 * (see `scripts/prerender-locale-helper.mjs`); this runtime emitter
 * mirrors them so SPA navigation between locales updates the head
 * without a reload and so `<title>` matches the active route.
 *
 * `path` is the locale-less canonical path component (e.g. `''` for
 * home, `'/privacy-policy'`, `'/stations/oxford-circus'`); the
 * component builds `https://<BASE>/<locale><path>` for the canonical
 * and each hreflang alternate.
 */
// The site's origin — the host of the city this build is for.
const SITE_BASE = CITY.apiOrigin

export type PageMetaProps = {
  locale: SupportedLocale
  /** Path WITHOUT the leading `/<locale>` and WITHOUT a trailing slash; `''` = locale root. */
  path: string
  title: string
  description: string
  /** Optional OG image override; defaults to the shared site banner. */
  ogImage?: string
  /** When true, emits `<meta name="robots" content="noindex,nofollow">`. */
  noindex?: boolean
  /**
   * Locales this path actually exists in — the hreflang cluster.
   *
   * Defaults to every supported locale. No surface is prerendered in all
   * 87 any more: home lives in `SiteUrls.HOME_LOCALES` (82), the legal
   * pages in English only (`SiteUrls.LEGAL_LOCALES`), and neither sets
   * its head here. It is WRONG for the
   * programmatic surface, prerendered in the 16 of
   * `SiteUrls.PROGRAMMATIC_LOCALES`, and WRONG for the station DETAIL
   * surface,
   * which exists in `SiteUrls.DETAIL_LOCALES` only (the rest 301 onto
   * it since TASK-1206; before that they were closed by `robots.txt`):
   * advertising all 87 there made Googlebot render
   * the page, follow the alternates and hit 3 881 disallowed
   * `/am/station/…`-shaped URLs, a count still climbing when it was
   * found (GSC 2026-09-07). The prerendered head has always carried the
   * correct 15; only this runtime emitter overrode it with 87.
   */
  hreflangLocales?: readonly string[]
  /**
   * `og:type` for this surface.
   *
   * Defaults to `website`. The station DETAIL surface is prerendered as
   * `place`, and until the prerendered head was marked `data-prerender`
   * in full the two coexisted — production served og:type "place" AND
   * "website" on the same page (2026-09-07). Now the runtime value is
   * the only one, so it has to be the right one.
   */
  ogType?: string
}

export function PageMeta({
  locale,
  path,
  title,
  description,
  ogImage = `${SITE_BASE}/og-image.png`,
  noindex = false,
  hreflangLocales = SUPPORTED_LOCALES,
  ogType = 'website',
}: PageMetaProps): ReactElement {
  const canonical = `${SITE_BASE}/${locale}${path}`
  // React 19 hoists Helmet's JSX head tags into <head> without touching
  // the prerendered static ones — GSC's rendered HTML showed a doubled
  // <title> on every detail page (2026-08-17). The prerender template
  // stamps its runtime-replaced tags `data-prerender`; clear them once
  // the runtime emitter owns the head.
  useEffect(() => {
    for (const el of document.head.querySelectorAll('[data-prerender]')) el.remove()
  }, [])
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      {hreflangLocales.map((alt) => (
        <link key={alt} rel="alternate" hrefLang={alt} href={`${SITE_BASE}/${alt}${path}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_BASE}/${DEFAULT_LOCALE}${path}`} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={localeToOgTag(locale)} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  )
}
